import { SxProps, Theme } from '@mui/material';
import { MEDIUM_GRAY, PRIMARY_MAIN, TEXT_SECONDARY } from '../../../styles/GlobalStyles';
import { CARD_SCROLLBAR_GAP_PX } from '../CardListScrollbar.constants';
import { cardListScrollbarStyles } from '../CardListScrollbar.styles';

/**
 * Styles for Masters page component
 */

const CARD_HIGHLIGHT_RAIL_WIDTH_PX = 8;

export const mastersStyles = {
  container: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: 'calc(100vh - 150px)',
      marginTop: '50px',
      padding: theme.spacing(2),
      overflow: 'hidden',
      boxSizing: 'border-box',
    }) as SxProps<Theme>,

  tabsContainer: (theme: Theme) =>
    ({
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
      paddingRight: `${CARD_SCROLLBAR_GAP_PX + CARD_HIGHLIGHT_RAIL_WIDTH_PX}px`,
      borderBottom: 1,
      borderColor: 'divider',
    }) as SxProps<Theme>,

  catalogStateFilterContainer: () =>
    ({
      minWidth: '180px',
      maxWidth: '240px',
      width: '220px',
      '& .MuiInputLabel-root': {
        transform: 'translate(14px, 11px) scale(1)',
      },
      '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    }) as SxProps<Theme>,

  tab: (theme: Theme) =>
    ({
      fontSize: '20px',
      fontWeight: theme.typography.fontWeightMedium,
      outline: 'none !important',
      boxShadow: 'none !important',
      border: 'none !important',
      '&:focus, &:active, &:focus-visible': {
        outline: 'none !important',
        boxShadow: 'none !important',
        border: 'none !important',
      },
      '&.Mui-focusVisible': {
        outline: 'none !important',
        boxShadow: 'none !important',
        border: 'none !important',
      },
    }) as SxProps<Theme>,

  tabContent: (theme: Theme) =>
    ({
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }) as SxProps<Theme>,

  breadcrumbContainer: (theme: Theme) =>
    ({
      marginBottom: theme.spacing(2),
      paddingLeft: `${CARD_HIGHLIGHT_RAIL_WIDTH_PX}px`,
      flexShrink: 0,
    }) as SxProps<Theme>,

  assignExperienceText: (theme: Theme) =>
    ({
      marginBottom: theme.spacing(1),
      paddingLeft: `${CARD_HIGHLIGHT_RAIL_WIDTH_PX}px`,
      color: TEXT_SECONDARY,
      fontSize: '18px',
      fontWeight: theme.typography.fontWeightRegular,
      flexShrink: 0,
    }) as SxProps<Theme>,

  manageCollectionsText: (theme: Theme) =>
    ({
      marginBottom: theme.spacing(1),
      paddingLeft: `${CARD_HIGHLIGHT_RAIL_WIDTH_PX}px`,
      color: TEXT_SECONDARY,
      fontSize: '18px',
      fontWeight: theme.typography.fontWeightRegular,
      flexShrink: 0,
    }) as SxProps<Theme>,

  scrollFrame: () =>
    ({
      flex: 1,
      minHeight: 0,
      position: 'relative',
      width: '100%',
    }) as SxProps<Theme>,

  scrollableList: (_theme: Theme) =>
    ({
      height: '100%',
      overflowY: 'auto',
      width: '100%',
      paddingRight: 0,
      boxSizing: 'border-box',
      ...cardListScrollbarStyles.hiddenNativeScrollbar(),
    }) as SxProps<Theme>,

  fixedScrollbarThumb: (_theme: Theme, top: number, isVisible: boolean) =>
    cardListScrollbarStyles.fixedScrollbarThumb(_theme, top, isVisible),

  breadcrumbLink: (theme: Theme) =>
    ({
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.primary.main,
      fontSize: '20px',
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    }) as SxProps<Theme>,

  breadcrumbIcon: (theme: Theme, iconUrl: string) =>
    ({
      marginRight: theme.spacing(0.5),
      width: '2.4rem',
      height: '2.4rem',
      display: 'inline-block',
      flexShrink: 0,
      backgroundColor: 'currentColor',
      maskImage: `url(${iconUrl})`,
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      maskSize: 'contain',
      WebkitMaskImage: `url(${iconUrl})`,
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      WebkitMaskSize: 'contain',
    }) as SxProps<Theme>,

  header: (theme: Theme) =>
    ({
      marginBottom: theme.spacing(3),
    }) as SxProps<Theme>,

  title: (theme: Theme) =>
    ({
      fontSize: theme.typography.h4.fontSize,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.text.primary,
    }) as SxProps<Theme>,

  sectionTitle: (theme: Theme) =>
    ({
      fontSize: theme.typography.h6.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.text.primary,
    }) as SxProps<Theme>,

  listContainer: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      width: '100%',
      paddingRight: `${CARD_SCROLLBAR_GAP_PX}px`,
      boxSizing: 'border-box',
    }) as SxProps<Theme>,

  collectionCardWrapper: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      cursor: 'pointer',
      width: '100%',
      '&::before': {
        content: '""',
        width: `${CARD_HIGHLIGHT_RAIL_WIDTH_PX}px`,
        flexShrink: 0,
        backgroundColor: 'transparent',
        borderTopLeftRadius: '3px',
        borderBottomLeftRadius: '3px',
        transition: 'background-color 0.2s ease',
        position: 'relative',
        zIndex: 1,
      },
      '&::after': {
        content: '""',
        width: `${CARD_HIGHLIGHT_RAIL_WIDTH_PX}px`,
        flexShrink: 0,
        backgroundColor: 'transparent',
        borderTopRightRadius: '3px',
        borderBottomRightRadius: '3px',
        transition: 'background-color 0.2s ease',
        position: 'relative',
        zIndex: 1,
      },
      '& .MuiCard-root': {
        width: `calc(100% - ${CARD_HIGHLIGHT_RAIL_WIDTH_PX * 2}px)`,
        transition: 'border-color 0.2s ease',
        boxShadow: `3px 3px 6px ${MEDIUM_GRAY}`,
      },
      '&:hover': {
        '&::before, &::after': {
          backgroundColor: PRIMARY_MAIN,
        },
        '& .MuiCard-root': {
          borderColor: 'transparent',
        },
      },
      '&.search-highlight': {
        '&::before, &::after': {
          backgroundColor: PRIMARY_MAIN,
        },
        '& .MuiCard-root': {
          borderColor: 'transparent',
        },
      },
    }) as SxProps<Theme>,

  templateCardWrapper: (theme: Theme, isHighlighted: boolean) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      cursor: 'pointer',
      width: '100%',
      '&::before': {
        content: '""',
        width: `${CARD_HIGHLIGHT_RAIL_WIDTH_PX}px`,
        flexShrink: 0,
        backgroundColor: isHighlighted ? PRIMARY_MAIN : 'transparent',
        borderTopLeftRadius: '3px',
        borderBottomLeftRadius: '3px',
        transition: 'background-color 0.2s ease',
        position: 'relative',
        zIndex: 1,
      },
      '&::after': {
        content: '""',
        width: `${CARD_HIGHLIGHT_RAIL_WIDTH_PX}px`,
        flexShrink: 0,
        backgroundColor: isHighlighted ? PRIMARY_MAIN : 'transparent',
        borderTopRightRadius: '3px',
        borderBottomRightRadius: '3px',
        transition: 'background-color 0.2s ease',
        position: 'relative',
        zIndex: 1,
      },
      '& .MuiCard-root': {
        width: `calc(100% - ${CARD_HIGHLIGHT_RAIL_WIDTH_PX * 2}px)`,
        boxShadow: `3px 3px 6px ${MEDIUM_GRAY}`,
      },
      '&:hover': {
        '&::before, &::after': {
          backgroundColor: PRIMARY_MAIN,
        },
        '& .MuiCard-root': {
          borderColor: 'transparent',
        },
      },
    }) as SxProps<Theme>,

  emptyState: (theme: Theme) =>
    ({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
      fontSize: theme.typography.body1.fontSize,
    }) as SxProps<Theme>,

  loadingContainer: (theme: Theme) =>
    ({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(4),
    }) as SxProps<Theme>,
};

export default mastersStyles;
